import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Filter, Sort } from "../interfaces/externalDataView";

interface ViewState {
  filters: Filter[];
  sort: Sort | undefined;
  currentPage: number;
  total: number | undefined;
}

interface MasterDataState {
  selectedViewId: string | undefined;
  viewStateById: Record<string, ViewState>;
  fetchTrigger: boolean;
}

const initialState: MasterDataState = {
  selectedViewId: undefined,
  viewStateById: {},
  fetchTrigger: false,
};

const getViewState = (state: MasterDataState, viewId: string): ViewState => {
  return (
    state.viewStateById[viewId] ?? {
      filters: [],
      sort: undefined,
      currentPage: 1,
      total: undefined,
    }
  );
};

export const masterDataSlice = createSlice({
  name: "masterData",
  initialState,
  reducers: {
    setSelectedViewId: (state, action: PayloadAction<string | undefined>) => {
      state.selectedViewId = action.payload;
    },
    setFilter: (
      state,
      action: PayloadAction<{ viewId: string; filter: Filter }>,
    ) => {
      const { viewId, filter } = action.payload;
      const viewState = getViewState(state, viewId);
      const existingIndex = viewState.filters.findIndex(
        (f) => f.field === filter.field,
      );

      if (existingIndex >= 0) {
        viewState.filters[existingIndex] = filter;
      } else {
        viewState.filters.push(filter);
      }

      state.viewStateById[viewId] = { ...viewState, currentPage: 1 };
    },
    removeFilter: (
      state,
      action: PayloadAction<{ viewId: string; field: string }>,
    ) => {
      const { viewId, field } = action.payload;
      const viewState = getViewState(state, viewId);
      viewState.filters = viewState.filters.filter((f) => f.field !== field);
      state.viewStateById[viewId] = { ...viewState, currentPage: 1 };
    },
    setSort: (
      state,
      action: PayloadAction<{ viewId: string; sort: Sort | undefined }>,
    ) => {
      const { viewId, sort } = action.payload;
      const viewState = getViewState(state, viewId);
      state.viewStateById[viewId] = { ...viewState, sort };
    },
    setCurrentPage: (
      state,
      action: PayloadAction<{ viewId: string; page: number }>,
    ) => {
      const { viewId, page } = action.payload;
      const viewState = getViewState(state, viewId);
      state.viewStateById[viewId] = { ...viewState, currentPage: page };
    },
    triggerFetch: (state) => {
      state.fetchTrigger = !state.fetchTrigger;
    },
  },
});

export const {
  setSelectedViewId,
  setFilter,
  removeFilter,
  setSort,
  setCurrentPage,
  triggerFetch,
} = masterDataSlice.actions;

export default masterDataSlice.reducer;
